import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { MessageCircle, Send, Settings, Clock, CheckCircle, XCircle } from 'lucide-react';

const TABS = [
  { id: 'send', label: 'Enviar', icon: Send },
  { id: 'config', label: 'Configuración', icon: Settings },
  { id: 'log', label: 'Historial', icon: Clock },
];

export default function MessagingPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('send');

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <MessageCircle size={22} className="text-green-400" /> Mensajería
        </h1>
        <p className="text-gray-400 text-sm mt-0.5">WhatsApp (Meta) y SMS · envío saliente</p>
      </div>

      <div className="flex gap-1 bg-gray-800/60 rounded-lg p-1 w-fit">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm transition-colors ${
                tab === t.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'send' && <SendTab qc={qc} />}
      {tab === 'config' && <ConfigTab qc={qc} />}
      {tab === 'log' && <LogTab />}
    </div>
  );
}

// ── Enviar ──────────────────────────────────────────────────
function SendTab({ qc }) {
  const [channel, setChannel] = useState('whatsapp');
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');

  const { data: channels } = useQuery('msg-channels', () => api.get('/messaging/channels').then(r => r.data));

  const sendMut = useMutation(
    (payload) => api.post('/messaging/send', payload).then(r => r.data),
    {
      onSuccess: () => { toast.success('Mensaje enviado'); setBody(''); qc.invalidateQueries('msg-log'); },
      onError: (e) => toast.error(e.response?.data?.error || 'Error al enviar'),
    }
  );

  const enabled = channels?.[channel]?.enabled;

  return (
    <div className="card max-w-xl space-y-4">
      <div className="flex gap-2">
        {['whatsapp', 'sms'].map(c => (
          <button key={c} onClick={() => setChannel(c)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
              channel === c ? 'bg-green-600/20 border-green-600 text-green-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}>
            {c === 'whatsapp' ? 'WhatsApp' : 'SMS'}
          </button>
        ))}
      </div>

      {!enabled && (
        <div className="text-xs text-yellow-400 bg-yellow-900/20 border border-yellow-800/40 rounded-lg p-2">
          El canal {channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} no está habilitado. Configúralo en la pestaña Configuración.
        </div>
      )}

      <div>
        <label className="label" htmlFor="msg-to">Destinatario</label>
        <input id="msg-to" className="input" value={to} onChange={e => setTo(e.target.value)}
          placeholder={channel === 'whatsapp' ? '573001234567 (con código de país)' : '+573001234567'} />
      </div>
      <div>
        <label className="label" htmlFor="msg-body">Mensaje</label>
        <textarea id="msg-body" className="input h-28 resize-none" value={body} onChange={e => setBody(e.target.value)}
          placeholder="Escribe el mensaje..." maxLength={1000} />
        <div className="text-xs text-muted mt-1">{body.length}/1000</div>
      </div>

      {channel === 'whatsapp' && (
        <p className="text-xs text-muted">
          Nota: WhatsApp solo permite texto libre dentro de la ventana de 24 h tras un mensaje del cliente.
          Para iniciar conversación se requieren <strong>plantillas aprobadas</strong> en Meta.
        </p>
      )}

      <button
        onClick={() => sendMut.mutate({ channel, to: to.trim(), body: body.trim() })}
        disabled={sendMut.isLoading || !to.trim() || !body.trim() || !enabled}
        className="w-full py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white rounded-lg font-medium flex items-center justify-center gap-2">
        <Send size={16} /> {sendMut.isLoading ? 'Enviando...' : 'Enviar'}
      </button>
    </div>
  );
}

// ── Configuración ───────────────────────────────────────────
function ConfigTab({ qc }) {
  const { data } = useQuery('msg-channels', () => api.get('/messaging/channels').then(r => r.data));
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <WhatsAppConfig data={data?.whatsapp} qc={qc} />
      <SmsConfig data={data?.sms} qc={qc} />
    </div>
  );
}

function WhatsAppConfig({ data, qc }) {
  const [form, setForm] = useState({ enabled: false, phone_number_id: '', access_token: '', api_version: 'v21.0' });
  useEffect(() => {
    if (data) setForm({
      enabled: data.enabled,
      phone_number_id: data.config?.phone_number_id || '',
      access_token: data.config?.access_token || '',
      api_version: data.config?.api_version || 'v21.0',
    });
  }, [data]);

  const mut = useMutation(
    () => api.put('/messaging/channels/whatsapp', {
      enabled: form.enabled,
      config: { phone_number_id: form.phone_number_id, access_token: form.access_token, api_version: form.api_version },
    }),
    { onSuccess: () => { toast.success('WhatsApp guardado'); qc.invalidateQueries('msg-channels'); },
      onError: () => toast.error('Error al guardar') }
  );

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-white">WhatsApp (Meta Cloud API)</h3>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
          Habilitado
        </label>
      </div>
      <div><label className="label" htmlFor="wa-phone-id">Phone Number ID</label>
        <input id="wa-phone-id" className="input" value={form.phone_number_id} onChange={e => setForm(f => ({ ...f, phone_number_id: e.target.value }))} placeholder="1234567890" /></div>
      <div><label className="label" htmlFor="wa-token">Access Token (permanente)</label>
        <input id="wa-token" className="input" type="password" value={form.access_token} onChange={e => setForm(f => ({ ...f, access_token: e.target.value }))} placeholder="EAAG..." autoComplete="new-password" /></div>
      <div><label className="label" htmlFor="wa-api-version">Versión API</label>
        <input id="wa-api-version" className="input" value={form.api_version} onChange={e => setForm(f => ({ ...f, api_version: e.target.value }))} placeholder="v21.0" /></div>
      <p className="text-xs text-muted">Obtén estos datos en tu App de Meta → WhatsApp → API Setup (WABA + número verificado).</p>
      <button onClick={() => mut.mutate()} disabled={mut.isLoading}
        className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Guardar WhatsApp</button>
    </div>
  );
}

function SmsConfig({ data, qc }) {
  const [form, setForm] = useState({ enabled: false, api_url: '', api_key: '', sender: '' });
  useEffect(() => {
    if (data) setForm({
      enabled: data.enabled,
      api_url: data.config?.api_url || '',
      api_key: data.config?.api_key || '',
      sender: data.config?.sender || '',
    });
  }, [data]);

  const mut = useMutation(
    () => api.put('/messaging/channels/sms', {
      enabled: form.enabled,
      config: { provider: 'msgrush', api_url: form.api_url, api_key: form.api_key, sender: form.sender },
    }),
    { onSuccess: () => { toast.success('SMS guardado'); qc.invalidateQueries('msg-channels'); },
      onError: () => toast.error('Error al guardar') }
  );

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-white">SMS (MsgRush)</h3>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
          Habilitado
        </label>
      </div>
      <div><label className="label" htmlFor="sms-api-url">URL de la API</label>
        <input id="sms-api-url" className="input" value={form.api_url} onChange={e => setForm(f => ({ ...f, api_url: e.target.value }))} placeholder="https://api.msgrush.com/..." /></div>
      <div><label className="label" htmlFor="sms-api-key">API Key / Token</label>
        <input id="sms-api-key" className="input" type="password" value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} placeholder="clave del proveedor" autoComplete="new-password" /></div>
      <div><label className="label" htmlFor="sms-sender">Remitente (Sender ID)</label>
        <input id="sms-sender" className="input" value={form.sender} onChange={e => setForm(f => ({ ...f, sender: e.target.value }))} placeholder="MiEmpresa" /></div>
      <p className="text-xs text-muted">Envía JSON <code>{'{ to, from, text }'}</code> con Bearer token. Ajustable si MsgRush usa otro formato.</p>
      <button onClick={() => mut.mutate()} disabled={mut.isLoading}
        className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Guardar SMS</button>
    </div>
  );
}

// ── Historial ───────────────────────────────────────────────
function LogTab() {
  const { data = [] } = useQuery('msg-log', () => api.get('/messaging/log').then(r => r.data), { refetchInterval: 15000 });
  return (
    <div className="card">
      <table className="w-full text-sm">
        <thead><tr>{['Fecha', 'Canal', 'Destino', 'Mensaje', 'Estado'].map(h => <th key={h} className="table-th">{h}</th>)}</tr></thead>
        <tbody>
          {data.map(m => (
            <tr key={m.id} className="table-row">
              <td className="table-td text-xs">{new Date(m.created_at).toLocaleString()}</td>
              <td className="table-td"><span className={`text-xs px-2 py-0.5 rounded-full ${m.channel === 'whatsapp' ? 'bg-green-900/40 text-green-400' : 'bg-blue-900/40 text-blue-400'}`}>{m.channel}</span></td>
              <td className="table-td font-mono text-xs">{m.to_number}</td>
              <td className="table-td text-gray-300 max-w-[280px] truncate">{m.body}</td>
              <td className="table-td">
                {m.status === 'sent'
                  ? <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle size={13} /> Enviado</span>
                  : <span className="flex items-center gap-1 text-red-400 text-xs" title={m.error}><XCircle size={13} /> Falló</span>}
              </td>
            </tr>
          ))}
          {!data.length && <tr><td colSpan={5} className="text-center py-10 text-muted">Sin mensajes</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
